import { useQuery, useMutation, useQueryClient } from 'react-query';
import { experimentApi } from 'services/api/aiReviewTest/experimentApi';
import { evaluationApi } from 'services/api/aiReviewTest/evaluationApi';
import { CreateExperimentDto, TestStatus } from 'types/aiReviewTest';
import { ToastManager } from 'components/organisms/CommonUI';

export const useCreateExperiment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateExperimentDto) => experimentApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['experiments']);
      ToastManager.show({
        content: '실험이 생성되었습니다.',
        duration: 2000,
      });
      
      // 실험 시작
      if (response.data.data.id) {
        experimentApi.start(response.data.data.id)
          .then(() => {
            ToastManager.show({
              content: '실험이 시작되었습니다.',
              duration: 2000,
            });
          })
          .catch((error) => {
            console.error('실험 시작 실패:', error);
            ToastManager.show({
              content: '실험은 생성되었지만 시작하는데 실패했습니다. 실험 목록에서 수동으로 시작해주세요.',
              duration: 5000,
            });
          });
      }
    },
    onError: (error: any) => {
      ToastManager.show({
        content: error.response?.data?.message || '실험 생성에 실패했습니다.',
        duration: 3000,
      });
    },
  });
};

export const useExperiments = (status?: TestStatus) => {
  return useQuery({
    queryKey: ['experiments', status],
    queryFn: () => experimentApi.findAll({ status }),
    staleTime: 30 * 1000, // 30초
    refetchOnWindowFocus: false, // 윈도우 포커스 시 자동 갱신 비활성화
  });
};

export const useRunningExperiments = () => {
  return useQuery({
    queryKey: ['experiments', 'running'],
    queryFn: () => experimentApi.findRunning(),
    refetchInterval: false, // 자동 갱신 비활성화
    refetchOnWindowFocus: false, // 윈도우 포커스 시 자동 갱신 비활성화
  });
};

export const useExperiment = (id: number | null) => {
  return useQuery({
    queryKey: ['experiments', id],
    queryFn: () => id ? experimentApi.findOne(id) : null,
    enabled: !!id,
  });
};

export const useExperimentProgress = (id: number | null, enabled = true) => {
  return useQuery({
    queryKey: ['experiments', id, 'progress'],
    queryFn: () => id ? experimentApi.getProgress(id) : null,
    enabled: !!id && enabled,
    refetchInterval: false, // 자동 갱신 비활성화
    refetchOnWindowFocus: false, // 윈도우 포커스 시 자동 갱신 비활성화
  });
};

export const useExperimentResults = (id: number | null) => {
  return useQuery({
    queryKey: ['experiments', id, 'results'],
    queryFn: () => id ? experimentApi.getResults(id) : null,
    enabled: !!id,
  });
};

export const useExperimentEvaluations = (id: number | null) => {
  return useQuery({
    queryKey: ['experiments', id, 'evaluations'],
    queryFn: () => id ? evaluationApi.findAll({ experimentId: id, withPatientId: true }) : null,
    enabled: !!id,
  });
};